/**
 * SalesChannelStep — Channel selection with editable commission %.
 * Default percentages pre-filled; artist overrides with ±5% stepper.
 * "Direct to Buyer" has no commission editor.
 */

import { T } from '@/styles/tokens';
import { StepNextButton } from './StepNextButton';
import { SectionIcon } from './SectionIcons';
import { SALES_CHANNEL_KEYS, CHANNEL_COMMISSION } from '@/constants/channels';
import type { SalesChannelKey } from '@/constants/channels';

interface SalesChannelStepProps {
  channel: SalesChannelKey;
  /** Custom commission % per channel (0–100). Only used for non-direct channels. */
  channelPcts: Record<string, number>;
  onChangeChannel: (key: SalesChannelKey) => void;
  onChangeChannelPct: (key: SalesChannelKey, pct: number) => void;
  onNext: () => void;
}

const cardStyle: React.CSSProperties = {
  background: T.white,
  borderRadius: 16,
  border: `1px solid ${T.cream200}`,
  padding: 20,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

export function SalesChannelStep({
  channel,
  channelPcts,
  onChangeChannel,
  onChangeChannelPct,
  onNext,
}: SalesChannelStepProps) {
  return (
    <div>
      <SectionIcon icon="channel" label="Sales channel" />
      <div style={cardStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {SALES_CHANNEL_KEYS.map((key) => {
            const selected = channel === key;
            const config = CHANNEL_COMMISSION[key];
            const isEditable = config.rate > 0;
            const pct = channelPcts[key] ?? Math.round(config.rate * 100);

            return (
              <div key={key}>
                {/* Radio row */}
                <label
                  onClick={() => onChangeChannel(key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    borderRadius: selected && isEditable ? '10px 10px 0 0' : 10,
                    cursor: 'pointer',
                    background: selected ? T.tealSoft : 'transparent',
                    border: `1px solid ${selected ? T.primary : T.cream200}`,
                    borderBottom: selected && isEditable ? `1px dashed rgba(61,171,165,0.25)` : undefined,
                    transition: 'all 0.15s ease',
                  }}
                >
                  <input
                    type="radio"
                    name="salesChannel"
                    checked={selected}
                    onChange={() => onChangeChannel(key)}
                    style={{ accentColor: T.primary, width: 16, height: 16 }}
                  />
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: selected ? 600 : 400,
                      color: selected ? T.text : T.charcoal600,
                      fontFamily: T.fontFamily,
                      flex: 1,
                    }}
                  >
                    {config.label.replace(/\s*\(\d+%\)/, '')}
                  </span>
                  {/* Show muted % on unselected editable channels */}
                  {isEditable && !selected && (
                    <span style={{ fontSize: 12, color: T.charcoal300, fontFamily: T.fontFamily }}>
                      {pct}%
                    </span>
                  )}
                </label>

                {/* Inline % editor — only when selected + editable */}
                {selected && isEditable && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 12px',
                      background: T.tealSoft,
                      border: `1px solid ${T.primary}`,
                      borderTop: 'none',
                      borderRadius: '0 0 10px 10px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        color: T.charcoal400,
                        fontFamily: T.fontFamily,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Commission
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onChangeChannelPct(key, Math.max(5, pct - 5));
                        }}
                        style={stepperBtnStyle}
                      >
                        −
                      </button>
                      <div style={pctDisplayStyle}>{pct}%</div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onChangeChannelPct(key, Math.min(90, pct + 5));
                        }}
                        style={stepperBtnStyle}
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <StepNextButton label="Next" disabled={false} onPress={onNext} />
      </div>
    </div>
  );
}

// ── Inline styles for stepper controls ──

const stepperBtnStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 8,
  border: `1px solid rgba(61,171,165,0.25)`,
  background: T.white,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 16,
  fontWeight: 600,
  color: T.primary,
  fontFamily: T.fontFamily,
  lineHeight: 1,
};

const pctDisplayStyle: React.CSSProperties = {
  width: 48,
  height: 28,
  borderRadius: 8,
  background: T.white,
  border: `1.5px solid ${T.primary}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  fontWeight: 700,
  color: T.text,
  fontFamily: T.fontFamily,
};
